import { useMutation, useQuery } from "@tanstack/react-query";
import { apiQueryAdapter } from "app/data/apiQueryAdapter";
import { ERROR_MESSAGES } from "app/data/common/errorMessages";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import { CopyLaneToDayPayload, CopyLaneToDayResponse, CreateLanePayload, CreateLaneResponse, ManifestsByDay, StaticLoadResponse } from "app/data/static-load/model";
import { toast } from "react-toastify";

export const useLanesByDay = (division: string, weekStartDate: string, dayOfWeek: string) => {

  const { data, isLoading, refetch } =
    useQuery({
      queryKey: [QUERY_KEYS.GET_LANES_BY_DAY, division, weekStartDate, dayOfWeek],
      queryFn: async () => {
        let params = {};

        if (division) {
          params = { division };
        }

        if (weekStartDate) {
          params = { ...params, weekStartDate };
        }

        const response = await apiQueryAdapter.query<ManifestsByDay>(`/manifest-management/lanes-by-day`,
          "GET",
          { params }
        );

        return response.data;

      },
      refetchOnWindowFocus: false,
      enabled: !!weekStartDate,
      retry: false,
    })
    
  return { data, isLoading, refetch };
}

export const useLaneByPlanID = (planId: string, division: string, weekStartDate: string) => {
  const { data, isLoading, refetch } =
    useQuery({
      queryKey: [QUERY_KEYS.GET_LANE_BY_PLAN_ID, planId, division, weekStartDate],
      queryFn: async () => {

        const response = await apiQueryAdapter.query<{
          lane: any;
          drops: any[];
        }>(`/manifest-management/lanes/${planId}`,
          "GET",
          { params: { division, weekStartDate } }
        );

        return response.data;

      },
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!planId && !!division && !!weekStartDate
    })
    
  return { data, isLoading, refetch };
}

export const useCreateLane = () => {
  return useMutation<
    CreateLaneResponse, // response type
    Error, // error type
    CreateLanePayload  // variables type
  >({
    mutationFn: async (payload) => {
      const response = await apiQueryAdapter.query<CreateLaneResponse>(
        `/manifest-management/lanes`,
        "POST",
        { body: payload }
      );

      return response.data;
    },
    onSuccess: () => {
      toast.info("Lane created successfully");
    },
    onError: (e: any) => {
      if (e instanceof Error) {
        toast.error(e.message);
      }
      else if (e?.response?.data?.message) {
        toast.error(e.response.data.message);
      }
      else {
        toast.error(ERROR_MESSAGES.COMMON);
      }
    },
  });
};

export const useDeleteLane = (onSuccess?: () => void) => {
  return useMutation<
    StaticLoadResponse, // response type
    Error, // error type
    string // variables type
  >({
    mutationFn: async (planId: string) => {
      const response = await apiQueryAdapter.query<StaticLoadResponse>(
        `/manifest-management/lanes/${planId}`,
        "DELETE"
      );

      return response.data;
    },
    onSuccess: () => {
      toast.info("Lane deleted successfully");
      onSuccess?.();
    },
    onError: (e: any) => {
      if (e instanceof Error) {
        toast.error(e.message);
      }
      else if (e?.response?.data?.message) {
        toast.error(e.response.data.message);
      }
      else {
        toast.error(ERROR_MESSAGES.COMMON);
      }
    },
  });
};

export const useMoveLane = () => {
  return useMutation<
    any, // response type
    Error, // error type
    any  // variables type
  >({
    mutationFn: async (payload) => {
      const response = await apiQueryAdapter.query<any>(
        `/manifest-management/lanes/${payload.planId}/move?targetWeekStartDate=${payload.targetWeekStartDate}`,
        "POST",
        { body: payload }
      );

      return response.data;
    },
    onSuccess: () => {
      toast.info("Lane moved successfully");
    },
    onError: (e: any) => {
      if (e instanceof Error) {
        toast.error(e.message);
      }
      else if (e?.response?.data?.message) {
        toast.error(e.response.data.message);
      }
      else {
        toast.error(ERROR_MESSAGES.COMMON);
      }
    },
  });
};

export const useCopyLaneToDay = () => {
  return useMutation<
    CopyLaneToDayResponse, // response type
    Error,                // error type
    CopyLaneToDayPayload  // variables type
  >({
    mutationFn: async (payload) => {
      const response = await apiQueryAdapter.query<CopyLaneToDayResponse>(
        "/manifest-management/lanes/copy-to-day",
        "POST",
        { body: payload }
      );

      return response.data;
    },
    onSuccess: () => {
      toast.info("Lane copied successfully");
    },
    onError: () => {
      toast.error("Failed to copy lane");
    },
  });
};